import React, { useState } from "react";
import { Fuel, Flame, Truck, Wind, Coins, Mountain, Gem } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";

const ENERGY_ICONS = [Fuel, Truck, Flame, Wind];
const SOLID_ICONS = [Coins, Mountain, Gem];

const Card = ({ item, Icon, activeLabel }) => {
  return (
    <article
      data-testid={`product-card-${item.name.split(" ")[0].toLowerCase()}`}
      className="card-vault p-7 md:p-8 relative"
    >
      <div className="flex items-start justify-between mb-6">
        <div className="inline-flex items-center justify-center w-12 h-12 border border-[#D4AF37]/40 text-[#D4AF37]">
          <Icon size={20} />
        </div>
        <span className="font-sans text-[10px] uppercase tracked text-steel" style={{ letterSpacing: "0.24em" }}>
          {item.tag}
        </span>
      </div>
      <h3 className="font-serif text-2xl md:text-[26px] text-bone mb-3 leading-tight" style={{ fontWeight: 500 }}>
        {item.name}
      </h3>
      <p className="font-sans text-sm md:text-[15px] text-steel leading-relaxed">
        {item.body}
      </p>
      <div className="mt-6 pt-5 border-t border-[#D4AF37]/12 flex items-center justify-between">
        <span className="font-sans text-[11px] uppercase tracked text-[#D4AF37]" style={{ letterSpacing: "0.28em" }}>
          {activeLabel}
        </span>
        <span className="status-dot" aria-hidden />
      </div>
    </article>
  );
};

const Products = () => {
  const { t } = useT();
  const [tab, setTab] = useState("energy");
  const items = tab === "energy" ? t.products.energy : t.products.solid;
  const icons = tab === "energy" ? ENERGY_ICONS : SOLID_ICONS;

  return (
    <section id="products" data-testid="products-section" className="relative py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="mb-14 md:mb-16 max-w-3xl">
          <div className="flex items-center gap-3 mb-6">
            <div className="hairline-crimson" />
            <span className="font-sans text-[11px] tracked uppercase text-[#D4AF37]" style={{ letterSpacing: "0.32em" }}>
              {t.products.eyebrow}
            </span>
          </div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-bone leading-[1.05] mb-5" style={{ fontWeight: 500 }}>
            {t.products.h2a}<br />
            <span className="text-[#D4AF37] italic">{t.products.h2b}</span>
          </h2>
          <p className="font-sans text-base md:text-lg text-steel leading-relaxed">
            {t.products.sub}
          </p>
        </div>

        <div
          role="tablist"
          aria-label="Product categories"
          className="inline-flex flex-wrap items-stretch border border-[#D4AF37]/30 bg-[#020C1B] mb-10 md:mb-12"
          data-testid="products-tablist"
        >
          {[
            { id: "energy", label: t.products.tab_energy },
            { id: "solid", label: t.products.tab_solid },
          ].map((tb) => {
            const active = tab === tb.id;
            return (
              <button
                key={tb.id}
                role="tab"
                aria-selected={active}
                data-testid={`products-tab-${tb.id}`}
                onClick={() => setTab(tb.id)}
                className={`font-sans uppercase text-[11px] md:text-[12px] tracked px-5 md:px-7 py-4 transition-colors duration-300 ${
                  active
                    ? "bg-[#D4AF37]/12 text-[#D4AF37] border-b-2 border-[#D4AF37]"
                    : "text-steel hover:text-bone border-b-2 border-transparent"
                }`}
                style={{ letterSpacing: "0.22em", minHeight: 52 }}
              >
                {tb.label}
              </button>
            );
          })}
        </div>

        <div
          data-testid="products-grid"
          className={`grid gap-6 md:gap-7 ${
            tab === "energy" ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
          }`}
        >
          {items.map((it, i) => (
            <Card key={it.name} item={it} Icon={icons[i] || Fuel} activeLabel={t.products.active} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Products;
